import { AppBar, Box, styled, Toolbar } from '@mui/material';
import { CandidateNavBarLogoContainer } from './CandidateNavBarLogoContainer';
import { getConnectUrlPath } from 'features/Connect/features/JobDiscovery/utils';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { useStateParam } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import auth from 'utils/auth';
import { useRouteMatch } from 'react-router-dom';
import { AuthenticatedAdminModal } from 'features/Connect/components/Modals/AuthenticatedAdminModal/AuthenticatedAdminModal';
import { ConnectAuthenticatedAdminModalDataTestIds } from 'data-testids/ConnectDataTestIds';
import { CandidateNavBarUnauthenticatedView, CandidateNavBarAuthenticatedView } from './Views';
import { ConnectModals } from 'features/Connect/utils/connectEnums';
import {
  connectRedirectUrls,
  nimbleConnectUrlFlag,
  candidatePortalRoutes,
} from 'utils/constants/urls';

export const CandidateNavBar: React.FC = () => {
  const jobBoardState = useStateParam();
  const connectUrlPath = getConnectUrlPath(jobBoardState);
  const isOnConnect = useRouteMatch({ path: `/${nimbleConnectUrlFlag}` });
  const isConnectLogin = useRouteMatch({ path: connectRedirectUrls.LOGIN, exact: true });
  const isConnectSignup = useRouteMatch({ path: connectRedirectUrls.SIGN_UP, exact: true });
  const isCandidatePortal = useRouteMatch({
    path: candidatePortalRoutes.CANDIDATE_PORTAL,
  });
  const { candidateMatches, connectModalsOpenState, matchQuizStatus, isFetchingMatchQuizStatus } =
    useConnectContext();
  const user = auth.getUser();
  const isCandidateUser = user?.profile ? auth.isCandidateUser(user) : false;

  return (
    <NavbarAppBar>
      <NavbarContainer
        maxWidth={!isConnectLogin && !isConnectSignup && !isCandidatePortal ? '1130px' : 'auto'}
      >
        <NavbarToolbar disableGutters>
          <CandidateNavBarLogoContainer
            jobBoardState={jobBoardState}
            connectUrlPath={connectUrlPath}
          />
          <Box>
            {user ? (
              <CandidateNavBarAuthenticatedView
                user={user}
                candidateMatches={candidateMatches}
                isFetchingMatchQuizStatus={isFetchingMatchQuizStatus}
                matchQuizStatus={matchQuizStatus}
                isOnConnect={!!isOnConnect}
                isCandidatePortal={!!isCandidatePortal}
                isCandidateUser={isCandidateUser}
              />
            ) : (
              <CandidateNavBarUnauthenticatedView />
            )}
          </Box>
        </NavbarToolbar>
      </NavbarContainer>
      {connectModalsOpenState[ConnectModals.AUTHENTICATED_ADMIN_MODAL] && (
        <AuthenticatedAdminModal
          jobBoardState={jobBoardState}
          district={user?.profile?.district}
          dataTestId={ConnectAuthenticatedAdminModalDataTestIds.NAVBAR_ADMIN_MODAL}
        />
      )}
    </NavbarAppBar>
  );
};

const NavbarAppBar = styled(AppBar)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  background: theme.palette.white.main,
  height: '80px',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.gray.light}`,
  padding: theme.spacing(0, 6),

  [theme.breakpoints.down('lg')]: {
    height: '64px',
    padding: theme.spacing(0, 2),
  },
}));

const NavbarContainer = styled(Box)(() => ({
  justifyContent: 'space-between',
  width: '100%',
}));

const NavbarToolbar = styled(Toolbar)(() => ({
  width: '100%',
  justifyContent: 'space-between',
  justifySelf: 'center',
}));
